import { defineStore } from "pinia";
import _ from "lodash";
import { DateTime } from "luxon";
import { api } from "@/api/base";
import config from "@/config";

import { useAuthStore } from "./auth";

import { editorLanguages, definedLanguages } from "@/i18n";

import {
  accessDataSchema,
  licenseDataSchema,
  userApiSchema,
  type User,
  type Membership,
  type GroupPublicInfo,
  membershipApiSchema,
  superUserCategory,
  accessTypeStringSchema,
  superAccessMappings,
  groupPublicInfoSchema,
} from "./validators";

export type AccessType = {
  groupId: string;
  accessType: string;
  creationTime: string;
  distance: number;
  isMember: boolean;
};

export type RefreshType = {
  refetch: boolean;
  questionnaireId: string;
  respondentIds?: string[];
  response?: any; // TODO please define better!
};

export type AccessState = {
  groupId: string;
  types: Array<{ accessType: string; isMember: boolean }>;
  categories: string[];
  fetchedTime: string;
};

export type LicenseState = {
  groupId: string;
  properties: Record<string, boolean | number>;
  fetchedTime: string;
  startTime?: string;
  endTime?: string;
};

export type RootState = {
  initialized: boolean | null;
  user: User | null;
  votes: Record<string, any>;
  access: Record<string, AccessState>;
  licenses: Record<string, LicenseState>;
  settings: Record<string, any>;
  hasGroups: boolean | null;
  activeGroupId: string | null;
  activeGroupName: string | null;
  activeGroupAccess: Record<string, any> | null;
  pendingInvitations: number | null;
  bulkEditPosts: any[];
  newAssetType: string | null;
  recentGroups: {
    groupId: string;
    groupName: string;
  }[];
  locale: string | null;
  rtl: boolean | null;
  editorLocale: string | null;
  editorRtl: boolean | null;
  currentMapView: { lat: number; lng: number; zoom: number } | null;
  superuserLicenseOverride: boolean;
  responseDataNeedsRefresh?: RefreshType;
};

// --- Default State (from index.ts) ---
const getDefaultState = (): RootState => {
  return {
    initialized: null,
    user: null,
    // Store project votes by projectId
    votes: {},
    access: {},
    licenses: {},
    settings: {},
    hasGroups: null,
    activeGroupId: null,
    activeGroupName: null,
    activeGroupAccess: null,
    pendingInvitations: null,
    recentGroups: [],
    locale: null,
    rtl: null,
    editorLocale: null,
    editorRtl: null,
    newAssetType: null,
    bulkEditPosts: [],
    // currentMapView holds the most recent map view at all times.
    currentMapView: null,
    // a boolean flag to indicate that a superuser wants to skip licensing checks
    superuserLicenseOverride: false,
    responseDataNeedsRefresh: undefined,
  };
};

export const useMainStore = defineStore("main", {
  state: (): RootState => getDefaultState(),

  // --- Getters (from getters.ts) ---
  getters: {
    isSuper(state) {
      return (type: string): boolean => {
        if (type === "any") {
          return !!(state.user?.superuser && !_.isEmpty(state.user.superuser));
        }
        const parsed = superUserCategory.safeParse(type);
        if (!parsed.success) {
          console.warn(`isSuper called with invalid type: ${type}`);
          return false;
        }
        return state.user?.superuser?.[parsed.data] === true;
      };
    },

    canSuper(state) {
      return (accessType: string): boolean => {
        const parsed = accessTypeStringSchema.safeParse(accessType);
        if (!parsed.success) {
          console.warn(`canSuper called with invalid accessType: ${accessType}`);
          return false;
        }
        const neededSuperType = superAccessMappings[parsed.data];
        const hasSuperBasedAccess = state.user?.superuser?.[neededSuperType] === true;
        return hasSuperBasedAccess;
      };
    },

    groupHas(state) {
      return (licenseType: string, groupId: string) => {
        const isSuperOverride = this.isSuper("any") && state.superuserLicenseOverride;
        if (isSuperOverride) return true;

        if (!groupId) return false;

        const license = state.licenses[groupId];

        const notFetched = _.isNil(license);
        if (notFetched) return null;

        return license?.properties[licenseType];
      };
    },

    has(state) {
      return (licenseType: string) => {
        const isSuperOverride = this.isSuper("any") && state.superuserLicenseOverride;
        if (isSuperOverride) return true;

        const groupId = state.activeGroupId;
        if (!groupId) return false;

        const license = state.licenses[groupId];

        const notFetched = _.isNil(license);
        if (notFetched) return null;

        return license?.properties[licenseType];
      };
    },

    hasAny(state) {
      return (explicitGroupId?: string) => {
        const isSuperOverride = this.isSuper("any") && state.superuserLicenseOverride;
        if (isSuperOverride) return true;

        const groupId = explicitGroupId || state?.activeGroupId;
        if (!groupId) return false;

        const license = state.licenses[groupId];
        if (!license) return false;

        const licenseIndex = Object.keys(license.properties).findIndex(
          (key) => license.properties[key] === true
        );
        if (licenseIndex !== -1) return true;

        const hasGlobalLicense = config?.license?.global?.length > 0;
        if (hasGlobalLicense) return true;

        return false;
      };
    },

    can(state) {
      return (accessType: string) => {
        // If you don't have a license of any kind, you can not do anything
        if (!this.hasAny()) return false;
        const groupId = state.activeGroupId;
        if (!groupId) return false;

        const access = state.access[groupId];
        if (!access) return null;

        const types = access.types;
        const index = _.findIndex(types, {
          accessType: accessType,
          isMember: true,
        });
        const hasMembershipBasedAccess = index !== -1;
        return hasMembershipBasedAccess || this.canSuper(accessType);
      };
    },

    dir(state): "rtl" | "ltr" {
      return state.rtl ? "rtl" : "ltr";
    },

    hasAppAccess(state): boolean {
      return !!state.hasGroups;
    },

    hasActiveGroup(state): boolean {
      return !!state.activeGroupId;
    },

    emailVerificationNeeded(state): boolean {
      const user = state.user;
      if (user && (user.facebookId || user.googleId || user.hasSuomiFiAuth)) {
        // These auth methods are strong enough, no email verification needed
        return false;
      }
      if (user && user.emailAddress && !user.emailVerified) {
        // Unverified emails may be typoed
        return true;
      }
      return false;
    },
  },

  // --- Actions (from mutations.ts + actions.ts) ---
  actions: {
    doNotShowBannerLinkAgain: (id: string) => {
      localStorage.setItem(`do-not-show-banner-${id}`, "true");
    },
    setSuperuserLicenseOverride(value: boolean) {
      this.superuserLicenseOverride = value;
    },
    updateUser(user: User) {
      if (this.user?.userId !== user?.userId) {
        // We have a previous users' local state an need to clean up the local
        // state before proceeding.
        this.access = {};
        this.activeGroupId = null;
        this.activeGroupName = null;
        this.recentGroups = [];
      }
      this.user = user;
    },
    clearUser() {
      this.user = null;
    },
    setBulkEditPosts(posts: any[]) {
      this.bulkEditPosts = posts;
    },
    setNewAssetType(assetType: string) {
      this.newAssetType = assetType;
    },
    setActiveGroup(group: { groupId: string; name?: string; groupName: string }) {
      this.activeGroupId = group.groupId;
      this.activeGroupName = group.name || group.groupName; // <-- /list and /get endpoints return different properties
      const index = _.findIndex(this.recentGroups, { groupId: group.groupId });
      const groupToInsert = {
        groupId: group.groupId,
        groupName: group.name || group.groupName, // <-- /list and /get endpoints return different properties
      };
      if (index === -1) {
        this.recentGroups.unshift(groupToInsert);
        this.recentGroups = _.slice(this.recentGroups, 0, 10);
      } else {
        _.remove(this.recentGroups, { groupId: group.groupId });
        this.recentGroups.unshift(groupToInsert);
      }
    },
    setAccess(_access: unknown) {
      const parsed = accessDataSchema.safeParse(_access);
      if (!parsed.success) {
        console.error("Failed to parse access data:", parsed.error);
        return;
      }
      const access = parsed.data;
      const groupId = access.groupId;
      const types = access.accessTypeList.map((t) => ({
        accessType: t.accessType,
        isMember: t.isMember,
      }));
      const categories = access.accessCategoryList;
      this.access = {
        ...this.access,
        [groupId]: {
          groupId: groupId,
          fetchedTime: DateTime.now().toISO(),
          types: types,
          categories: categories,
        },
      };
    },
    setLicense(_license: unknown) {
      const parsed = licenseDataSchema.safeParse(_license);
      if (!parsed.success) {
        console.error("Failed to parse license data:", parsed.error);
        return;
      }
      const license = parsed.data;
      const groupId = license.groupId || license.rootGroupId;
      if (!groupId) {
        console.error("License data missing groupId and rootGroupId:", license);
        return;
      }
      this.licenses = {
        ...this.licenses,
        [groupId]: {
          groupId: groupId,
          fetchedTime: DateTime.now().toISO(),
          properties: {},
        },
      };
      license.licenseProperties.forEach((l) => {
        if (l.expired) return;
        this.licenses[groupId].properties = {
          ...this.licenses[groupId].properties,
          [l.key]: l.value,
        };
        // does not handle multiple concurrent licenses
        if (l.key.includes("tier")) {
          this.licenses[groupId].startTime = l.startTime;
          this.licenses[groupId].endTime = l.endTime;
        }
      });
    },
    deactivateActiveGroup() {
      if (!this.activeGroupId) return;
      delete this.access[this.activeGroupId];
      this.activeGroupId = null;
      this.activeGroupName = null;
    },
    setUserHasGroups(hasGroups: boolean) {
      this.hasGroups = hasGroups;
      if (!hasGroups) {
        this.access = {};
        this.activeGroupId = null;
        this.activeGroupName = null;
        this.recentGroups = [];
      }
    },
    setLanguage(value: string) {
      const language = _.find(definedLanguages, { code: value });
      if (language) {
        this.locale = value;
        this.rtl = !!language?.rtl;
      }
    },
    setEditorLanguage(value: string) {
      const language = _.find(editorLanguages, { code: value });
      if (language) {
        this.editorLocale = value;
        this.editorRtl = !!language?.rtl;
      }
    },
    clearMapView() {
      this.currentMapView = null;
    },
    setMapView(view: { lat: number; lng: number; zoom: number }) {
      if (!view || !_.isFinite(+view.lat) || !_.isFinite(+view.lng) || !_.isFinite(+view.zoom))
        return;
      this.currentMapView = view;
    },
    setResponseDataNeedsRefresh(value: RefreshType) {
      this.responseDataNeedsRefresh = value;
    },

    async initApp(params: { force: boolean }) {
      // If we already did this, do not do it again unless forced to
      if (this.initialized && !params?.force) return;
      let user: User | null = null;
      let memberships: Membership | null = null;
      const authStore = useAuthStore();
      if (authStore.isAuthenticated) {
        try {
          const resp = await api.post("/user/self/get");
          user = userApiSchema.parse(resp.data.response);
        } catch (error) {
          console.error("Failed to get user info inside initApp:", error);
          this.clearUser();
          await authStore.logout();
        }
      }

      if (user) {
        this.updateUser(user);

        // check if user has any groups
        try {
          const options = {
            count: true,
            limit: 1, // <-- fetch 1 so we can switch to it if needed
          };
          const resp = await api.post("/user/self/membership/list", options);
          memberships = membershipApiSchema.parse(resp.data.response);
        } catch (error) {
          console.error(error);
        }

        // check if user has pending invites, this can be done async
        // We call the action but don't await it, so it runs in parallel
        this.updatePendingInvitations();
      }

      // check if we got a groups response and set up stuff accordingly
      if (!memberships) {
        this.setUserHasGroups(false);
      } else {
        const count = memberships.count || 0;
        this.setUserHasGroups(count > 0);

        // for convenience, iff user has 1 group, make it the active group
        const group = memberships.groups[0];
        if (count === 1 && group.requestAccepted) {
          this.setActiveGroup(group);
          await this.getLicenses(group.groupId);
          await this.getAccessLevels(group.groupId);
        }
        // else check if we have a groupId stored, try to fetch it and set up as
        // active group
        else if (this.activeGroupId) {
          const groupId = this.activeGroupId;
          let existingGroup: GroupPublicInfo | null = null;
          try {
            const resp = await api.post("/user/group/view", { groupId });
            existingGroup = groupPublicInfoSchema.parse(resp.data.response);
          } catch (error) {
            this.deactivateActiveGroup();
          }
          if (existingGroup) {
            this.setActiveGroup({ ...existingGroup, groupName: existingGroup.name });
            await this.getLicenses(existingGroup.groupId);
            await this.getAccessLevels(existingGroup.groupId);
          } else {
            console.error("Could not find group");
            // throw new Error("Group not found");
          }
        }
        // else fall back to recent groups
        else if (Array.isArray(this.recentGroups) && this.recentGroups?.length > 0) {
          const fallbackGroup = this.recentGroups[0];
          this.setActiveGroup(fallbackGroup);
          await this.getLicenses(fallbackGroup.groupId);
          await this.getAccessLevels(fallbackGroup.groupId);
        }
      }

      // all done, set flag to not do this again
      this.initialized = true;
    },

    async updatePendingInvitations() {
      // check if we have pending groups, this can be done async
      try {
        const response = await api.post("/user/self/membership/list", {
          count: true,
          limit: 0,
          filter: {
            groupAccepted: { is: true },
            userAccepted: { is: false },
          },
        });
        this.pendingInvitations = response.data.response.count;
      } catch (error) {
        console.error("Failed to update pending invitations:", error);
      }
    },

    async getAccessLevels(groupId: string) {
      try {
        const response = await api.post("/user/self/access/get", { groupId });
        this.setAccess(response.data.response);
        return response;
      } catch (error) {
        this.deactivateActiveGroup();
        throw error;
      }
    },

    async getLicenses(groupId: string) {
      // return here is necessary so route guard can hook into api.then()
      try {
        const response = await api.post("/user/group/license/current/get", { groupId });
        this.setLicense(response.data.response);
      } catch (error) {
        console.error("Failed to get licenses:", error);
        // Handle error appropriately
      }
    },
  },

  // --- Persistence (from index.ts) ---
  persist: {
    key: "maptionnaire-main-store", // Based on vuex-persistedstate setup
    storage: localStorage,
    pick: [
      "rtl",
      "locale",
      "licenses",
      "editorRtl",
      "editorLocale",
      "user",
      "recentGroups",
      "activeGroupId",
      "activeGroupName",
      "activeGroupAccess",
    ],
  },
});
